/**
 * user_devices — audit-only device-binding table (D-08 / D-09 / D-10).
 *
 * Phase 4 / Plan 02: shipped via migration 0003_phase4_auth_tables.sql.
 *
 * Belt-and-suspenders auth (RESEARCH § Architecture Pattern 2): Cognito
 * Remembered Devices is the ENFORCEMENT layer — it issues the SMS-OTP
 * challenge on a new device during the auth flow itself. This table is the
 * AUDIT layer — it captures every (user, device) pair we observe via the
 * cognitoJwtPlugin's first-seen check, supports "sign me out of all devices"
 * UX, and ties into the Phase 5 audit-event chain.
 *
 * NOT tenant-scoped (D-09):
 *   Devices follow the USER, not a tenant. A driver who works for two haulers
 *   in two tenants logs in from one phone — we want a single user_devices row
 *   for that (user, device), not two. So no `tenant_id` column and no RLS
 *   policy. Cross-user safety is enforced at the API layer:
 *   `cognitoJwtPlugin` only ever queries WHERE `user_id = req.userId` from
 *   the verified JWT.
 *
 * mode CHECK (D-09):
 *   `mode IN ('PERSONAL', 'SHARED', 'WEB')` — the canonical 3-mode set,
 *   pinned in @lodex-pro/shared/auth/device-modes (DEVICE_MODES const).
 *   Plan 04-04 ships `assertDeviceModesHealthy()` which reads the SQL CHECK
 *   at boot and fails the server start if the IN-clause drifts from the TS
 *   literal list (parity with Phase 2 D-08's JOB_STATUS_CODES check and
 *   Phase 3 D-06's ROLE_CODES check).
 *
 * device_id_hash (D-10):
 *   Per-user salted SHA-256 of the raw device fingerprint:
 *   `sha256(${userId}:${deviceId})`. Salting prevents cross-user device-ID
 *   inference if the table ever leaks; the API never stores the raw
 *   fingerprint.
 *
 * UNIQUE (user_id, device_id_hash):
 *   One row per (user, device) pair. The cognitoJwtPlugin's first-seen flow
 *   relies on this uniqueness — re-observation of an existing device updates
 *   last_seen_at, NEVER inserts a duplicate row. Cascade on user delete is
 *   intentional: a deleted user's device history dies with them (Phase 5
 *   audit-events table retains the immutable chain for compliance).
 *
 * No CREATE POLICY block:
 *   RLS declarations are deliberately omitted. The 0003 migration does NOT
 *   enable RLS on this table; cross-user safety is API-enforced.
 *
 * Sources:
 *   - CONTEXT D-08 (belt-and-suspenders), D-09 (schema verbatim),
 *     D-10 (first-seen flow), D-11 (device fingerprint sources)
 *   - RESEARCH § Architecture Pattern 2; § Code Examples Example 2 (this file)
 *   - master spec §7 (authentication flows), §32 (audit trail)
 *   - gsd/DECISIONS_LOG.md DEC-004 (UTC display rule — every timestamp
 *     here is `with timezone`)
 */

import { check, index, pgTable, text, timestamp, uniqueIndex, uuid } from 'drizzle-orm/pg-core';
import { sql } from 'drizzle-orm';
import { createInsertSchema, createSelectSchema } from 'drizzle-zod';
import { users } from './users.js';
import { DEVICE_MODES } from '../../auth/device-modes.js';

export const userDevices = pgTable(
  'user_devices',
  {
    id: uuid('id').primaryKey().defaultRandom(),
    userId: uuid('user_id')
      .notNull()
      .references(() => users.id, { onDelete: 'cascade' }),
    // sha256(userId:deviceId) — per-user salted hash, never raw fingerprint
    deviceIdHash: text('device_id_hash').notNull(),
    // CHECK constraint enforced in (t) => [...] callback below
    mode: text('mode').notNull(),
    // user-supplied label OR auto-derived from User-Agent at first-seen
    name: text('name'),
    userAgent: text('user_agent'),
    firstSeenAt: timestamp('first_seen_at', { withTimezone: true }).notNull().defaultNow(),
    lastSeenAt: timestamp('last_seen_at', { withTimezone: true }).notNull().defaultNow(),
    // NULL = active; non-NULL = "sign me out of all devices" or admin revoke
    revokedAt: timestamp('revoked_at', { withTimezone: true }),
  },
  (t) => [
    // One row per (user, device) pair — first-seen check relies on this
    uniqueIndex('user_devices_user_hash_idx').on(t.userId, t.deviceIdHash),
    // Supports "list all my devices" UX (settings → connected devices)
    index('user_devices_user_idx').on(t.userId),
    // CHECK constraint name `user_devices_mode_check` is asserted by
    // plan 04-04's `assertDeviceModesHealthy()` boot check — keep stable.
    check(
      'user_devices_mode_check',
      sql.raw(`mode IN (${DEVICE_MODES.map((m) => `'${m}'`).join(', ')})`),
    ),
    // No RLS policy — user_devices is NOT tenant-scoped (devices follow the user).
    // Cross-user safety enforced at API layer (cognitoJwtPlugin keys queries on req.userId).
  ],
);

export type UserDevice = typeof userDevices.$inferSelect;
export type NewUserDevice = typeof userDevices.$inferInsert;
export const userDeviceSelectSchema = createSelectSchema(userDevices);
export const userDeviceInsertSchema = createInsertSchema(userDevices);
